/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { GSLBEnum } from '../enums';
import { GslbBackendModel, GslbFrontendModel, GslbRealServiceModel, GslbTopologyModel } from '../models';
import { HealthCheckModel } from '../models/healthcheck.model';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';

@Injectable()
export class GslbApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get the frontends.
     * @param options
     */
    public getFrontends(options?: any): Observable<GslbFrontendModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_FRONTENDS, moduleAuth)
                    .subscribe({
                        next: (response: GslbFrontendModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a frontend.
     * @param service
     * @param options
     */
    public addFrontend(service: GslbFrontendModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.apiService.post(GSLBEnum.POST_GSLB_FRONTEND, service, moduleAuth)
                    .subscribe({
                        next: (response: GslbFrontendModel): void => {
                            observer.next({ vip: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Edit a frontend.
     * @param globalName
     * @param options
     */
    public updateFrontend(globalName: GslbFrontendModel, options?: any): Observable<GslbFrontendModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(GSLBEnum.PUT_GSLB_FRONTEND(globalName.id), globalName, moduleAuth)
                    .subscribe({
                        next: (response: GslbFrontendModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete a frontend.
     * @param id
     * @param options
     */
    public deleteFrontend(id: string, options?: any): Observable<string> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(GSLBEnum.DELETE_GSLB_FRONTEND(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get backends.
     * @param options
     */
    public getBackends(options?: any): Observable<GslbBackendModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_BACKENDS, moduleAuth)
                    .subscribe({
                        next: (response: GslbBackendModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a backend.
     * @param backend
     * @param options
     */
    public addBackend(backend: GslbBackendModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.apiService.post(GSLBEnum.POST_GSLB_BACKEND, backend, moduleAuth)
                    .subscribe({
                        next: (response: GslbBackendModel): void => {
                            observer.next({ pool: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Edit a backend.
     * @param backend
     * @param options
     */
    public updateBackend(backend: GslbBackendModel, options?: any): Observable<GslbBackendModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(GSLBEnum.PUT_GSLB_BACKEND(backend.id), backend, moduleAuth)
                    .subscribe({
                        next: (response: GslbBackendModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete a backend.
     * @param id
     * @param options
     */
    public deleteBackend(id: string, options?: any): Observable<string> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(GSLBEnum.DELETE_GSLB_BACKEND(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get real servers
     * @param options
     */
    public getRealServers(options?: any): Observable<GslbRealServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_RIPS, moduleAuth)
                    .subscribe({
                        next: (response: GslbRealServiceModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a real server.
     * @param rip
     * @param options
     */
    public addRealServer(rip: GslbRealServiceModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
                this.apiService.post(GSLBEnum.POST_GSLB_RIP, rip, moduleAuth)
                    .subscribe({
                        next: (response: GslbRealServiceModel): void => {
                            observer.next({ rip: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Edit a real server.
     * @param rip
     * @param options
     */
    public updateRealServer(rip: GslbRealServiceModel, options?: any): Observable<GslbRealServiceModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(GSLBEnum.PUT_GSLB_RIP(rip.id), rip, moduleAuth)
                    .subscribe({
                        next: (response: GslbRealServiceModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete a real server.
     * @param id
     * @param options
     */
    public deleteRealServer(id: string, options?: any): Observable<string> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(GSLBEnum.DELETE_GSLB_RIP(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get topologies.
     * @param options
     */
    public getTopologies(options?: any): Observable<GslbTopologyModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_TOPOLOGIES, moduleAuth)
                    .subscribe({
                        next: (response: GslbTopologyModel[]): void => {
                            // Returning gslb Topologies
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a topology.
     * @param topology
     * @param options
     */
    public addTopology(topology: GslbTopologyModel, options?: any): Observable<GslbTopologyModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(GSLBEnum.POST_GSLB_TOPOLOGY, topology, moduleAuth)
                    .subscribe({
                        next: (response: GslbTopologyModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Edit a topology.
     * @param topology
     * @param options
     */
    public editTopology(topology: GslbTopologyModel, options?: any): Observable<GslbTopologyModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(GSLBEnum.PUT_GSLB_TOPOLOGY(topology.id), topology, moduleAuth)
                    .subscribe({
                        next: (response: GslbTopologyModel): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete a topology.
     * @param id
     * @param options
     */
    public deleteTopology(id: string, options?: any): Observable<string> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(GSLBEnum.DELETE_GSLB_TOPOLOGY(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get GSLB health check report from API.
     */
    public getHealthChecksReport(options?: any): Observable<HealthCheckModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_HEALTHCHECK_REPORTS, moduleAuth, true)
                    .subscribe({
                        next: (response: HealthCheckModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get GSLB distribution state report from the API.
     */
    public getDistributionStateReport(options?: any): Observable<string> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(GSLBEnum.GET_GSLB_DISTRIBUTION_REPORTS, moduleAuth, true)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }
}
